import React, { useState } from "react";
import Sidebar from "./Sidebar";
import MobileNav from "./MobileNav";
import { useAuth } from "@/context/AuthContext";
import { Menu, Bell, LogOut, User, ChevronDown } from "lucide-react";

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  return (
    <div className="flex h-screen bg-slate-50 overflow-hidden">
      {/* Desktop Sidebar */}
      <div className="hidden lg:flex lg:flex-shrink-0">
        <div className="w-60 sidebar flex flex-col">
          <Sidebar />
        </div>
      </div>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-black/60" onClick={() => setSidebarOpen(false)} />
          <div className="absolute left-0 top-0 h-full w-60 sidebar flex flex-col z-50">
            <Sidebar onClose={() => setSidebarOpen(false)} />
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top Bar */}
        <header className="bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between flex-shrink-0 z-30">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 rounded-sm hover:bg-slate-100 text-slate-600"
              data-testid="sidebar-toggle"
            >
              <Menu size={20} />
            </button>
            <div className="hidden sm:block">
              <div className="text-xs font-bold text-orange-500 tracking-widest uppercase">INOVIT</div>
              <div className="text-[10px] text-slate-400 leading-none">Smart Meter ERP</div>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {user?.circle_name && (
              <span className="hidden sm:inline-flex items-center px-2 py-1 rounded-sm bg-orange-50 text-orange-700 text-xs font-semibold border border-orange-200">
                {user.circle_name}
              </span>
            )}
            <button className="p-2 rounded-sm hover:bg-slate-100 text-slate-500 relative" data-testid="notifications-btn">
              <Bell size={18} />
              <span className="absolute top-1 right-1 w-2 h-2 bg-orange-500 rounded-full"></span>
            </button>
            <div className="relative">
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center gap-2 px-3 py-1.5 rounded-sm hover:bg-slate-100 transition-colors"
                data-testid="user-menu-btn"
              >
                <div className="w-7 h-7 rounded-sm bg-orange-500 flex items-center justify-center text-white text-xs font-bold">
                  {user?.full_name?.charAt(0) || user?.role?.charAt(0)?.toUpperCase() || "U"}
                </div>
                <div className="hidden sm:block text-left">
                  <div className="text-xs font-semibold text-slate-700 leading-none">{user?.full_name?.split(" ")[0] || "User"}</div>
                  <div className="text-[10px] text-slate-400 uppercase tracking-wide">{user?.role}</div>
                </div>
                <ChevronDown size={14} className="text-slate-400" />
              </button>
              {userMenuOpen && (
                <div className="absolute right-0 mt-1 w-44 bg-white border border-slate-200 rounded-sm shadow-lg z-50">
                  <div className="px-3 py-2 border-b border-slate-100">
                    <div className="text-xs font-semibold text-slate-700">{user?.full_name}</div>
                    <div className="text-[10px] text-slate-400">{user?.role?.toUpperCase()}</div>
                  </div>
                  <button
                    onClick={logout}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                    data-testid="logout-btn"
                  >
                    <LogOut size={14} />
                    Sign out
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-auto pb-16 lg:pb-0" data-testid="main-content">
          {children}
        </main>
      </div>

      {/* Mobile Bottom Nav */}
      <MobileNav />
    </div>
  );
}
